import { useState, useCallback } from "react";

import {
  CheckIcon,
  ClearIcon,
  COLORS,
  EditIcon,
  V2EmptyState,
} from "@stacklet/ui";
import { graphql, usePaginationFragment } from "react-relay";
import { generatePath } from "react-router";

import { HumanTimestamp } from "app/components/HumanTimestamp";
import { Link, TableLink } from "app/components/links";
import { XGridTable } from "app/components/table/XGridTable";
import { ToolbarHeader } from "app/components/ToolbarHeader";
import { paths } from "app/constants/paths";
import { useHasScopePermission, useLoadNext } from "app/hooks";
import { useClientSideSortableGrid } from "app/hooks/grid/useClientSideSortableGrid";

import {
  type UserList_users$data,
  type UserList_users$key,
} from "./__generated__/UserList_users.graphql";
import { CreateNonSsoUser } from "./components/actions/CreateNonSsoUser";
import { RemoveSelectedUsers } from "./components/actions/RemoveSelectedUsers";

import type { UserListRefetchQuery } from "./__generated__/UserListRefetchQuery.graphql";
import type { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import type { DeepWriteable } from "types/utils";

interface Props {
  queryRef: UserList_users$key;
  hasToolbar?: boolean;
  hasRoles?: boolean;
  userType?: "sso" | "non-sso";
}

export type User = UserList_users$data["users"]["edges"][0]["node"];

export function UserList({
  queryRef,
  hasRoles = true,
  hasToolbar = true,
  userType,
}: Props) {
  const [selectedRows, setSelectedRows] = useState<User[]>([]);

  const isSsoUsers = userType === "sso";
  const isNonSsoUsers = userType === "non-sso";
  const isAllUsers = userType === undefined;

  const hasPermission = useHasScopePermission();
  const canEdit = hasPermission("system", "EDIT");

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    UserListRefetchQuery,
    UserList_users$key
  >(
    graphql`
      fragment UserList_users on Query
      @relay(mask: false)
      @argumentDefinitions(
        first: { type: "Int" }
        last: { type: "Int" }
        after: { type: "String" }
        before: { type: "String" }
        filterElement: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "UserListRefetchQuery") {
        users(
          first: $first
          last: $last
          after: $after
          before: $before
          filterElement: $filterElement
        ) @connection(key: "UserList_users") {
          edges {
            node {
              id
              name
              lastLogin
              key
              ssoUser
              roleAssignments {
                edges {
                  node {
                    role {
                      name
                    }
                  }
                }
              }
              userGroups(first: 100) {
                edges {
                  node {
                    uuid
                    name
                    displayName
                  }
                }
              }
            }
          }
          pageInfo {
            total
          }
        }
      }
    `,
    queryRef,
  );

  const loadMore = useLoadNext(hasNext, loadNext);

  // Use client-side sorting with URL persistence
  const [sortModel, setSortModel] = useClientSideSortableGrid();

  const groupsSortComparator = (
    v1: DeepWriteable<UserRow["groups"]>,
    v2: DeepWriteable<UserRow["groups"]>,
  ) => v1[0]?.name?.localeCompare(v2[0]?.name ?? "");

  const sortComparator = (v1: string[], v2: string[]) =>
    v1[0].localeCompare(v2[0]);

  const rows = (data?.users.edges ?? [])
    .filter((edge) => edge.node)
    .map((edge) => {
      const { node } = edge;
      return {
        ...node,
        uuid: node.id,
        name: node.name,
        lastLogin: node.lastLogin,
        roles: [
          ...new Set(
            (node.roleAssignments.edges ?? []).map(
              (edge) => edge.node.role.name,
            ),
          ),
        ],
        groups: (node.userGroups.edges ?? []).map((edge) => edge.node),
        key: node.key,
      };
    });

  type UserRow = (typeof rows)[0];
  const columns: GridColDef<UserRow>[] = [
    {
      headerName: "User",
      field: "name",
      flex: 1,
      renderCell: (params) => {
        const { key, name } = params.row;

        return name ? (
          <TableLink
            name={name}
            to={generatePath(paths.userDetails, { key: String(key) })}
          />
        ) : (
          <span>{key}</span>
        );
      },
    },
    ...(isAllUsers
      ? [
          {
            headerName: "SSO User",
            field: "ssoUser",
            flex: 0.5,
            renderCell: (params) => {
              const { ssoUser } = params.row;
              return ssoUser ? (
                <CheckIcon
                  aria-label="sso user"
                  style={{ color: COLORS.green.D30 }}
                />
              ) : (
                <ClearIcon
                  aria-label="non-sso user"
                  style={{ color: COLORS.red.base }}
                />
              );
            },
          } as GridColDef<UserRow>,
        ]
      : []),
    {
      headerName: "Roles",
      field: "roles",
      flex: 0.5,
      renderCell: (params) => {
        const { roles } = params.row;
        return roles.length > 0 ? <span>{roles.join(", ")}</span> : "--";
      },
      sortable: true,
      sortComparator,
    },
    {
      headerName: "Last Login",
      field: "lastLogin",
      flex: 0.5,
      renderCell: (params) => {
        const { lastLogin } = params.row;
        if (lastLogin) {
          return <HumanTimestamp timestamp={params.row.lastLogin} />;
        }
      },
    },
    {
      headerName: "Groups",
      field: "groups",
      flex: 1,
      renderCell: (params) => {
        const { groups } = params.row;
        return groups.length > 0 ? (
          <span>
            {groups.map((group, index) => (
              <span key={group.name}>
                {index > 0 && ", "}
                <TableLink
                  name={group.displayName || group.name}
                  to={generatePath(paths.groupDetails, {
                    groupUuid: group.uuid,
                  })}
                />
              </span>
            ))}
          </span>
        ) : (
          "--"
        );
      },
      sortable: true,
      sortComparator: groupsSortComparator,
    },
    ...(isAllUsers && canEdit
      ? [
          {
            headerName: "",
            field: "actions",
            flex: 0.5,
            sortable: false,
            renderCell: (params) => {
              const { key, ssoUser } = params.row;
              return !ssoUser ? (
                <div className="flex w-full justify-end">
                  <Link
                    textDecoration="none"
                    to={generatePath(paths.userEdit, { key: String(key) })}
                  >
                    <span className="text-legacy-neutrals-ash">
                      <EditIcon className="h-4 w-4" />
                    </span>
                  </Link>
                </div>
              ) : null;
            },
          } as GridColDef<UserRow>,
        ]
      : []),
  ];

  const updateSelectedModel = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      const selectedIDs = newSelectionModel as string[];
      const filtered = rows.filter((row) => selectedIDs.includes(row.id));
      setSelectedRows(filtered as unknown as User[]);
    },
    [rows],
  );

  const rowCount = data.users.pageInfo.total || rows.length;

  const columnVisibilityModel = {
    groups: isSsoUsers || isAllUsers,
    roles: hasRoles,
  };

  return (
    <>
      {isNonSsoUsers ? (
        <ToolbarHeader hideSearchBar>
          <div className="flex items-center gap-lg">
            <RemoveSelectedUsers selectedRows={selectedRows} />
            <CreateNonSsoUser />
          </div>
        </ToolbarHeader>
      ) : null}
      {rowCount === 0 ? (
        <V2EmptyState message="No users exist yet." />
      ) : (
        <div className="h-4/5">
          <XGridTable
            checkboxSelection={isNonSsoUsers}
            columns={columns}
            getRowId={(row) => row.uuid}
            hasToolbar={hasToolbar}
            initialState={{
              columns: {
                columnVisibilityModel,
              },
            }}
            loading={isLoadingNext}
            onRowSelectionModelChange={updateSelectedModel}
            onRowsScrollEnd={loadMore}
            onSortModelChange={setSortModel}
            rowCount={rowCount}
            rowSelectionModel={selectedRows.map((row) => row.id)}
            rows={rows}
            sortModel={sortModel || []}
            tableId="admin-users-user-list"
          />
        </div>
      )}
    </>
  );
}
